import { Color, FrontSide, MeshPhysicalMaterial, MeshStandardMaterial, Vector2 } from 'three';

import { DESIGN_PACKAGE_FORCE_ENVMAP_INTENSITY, DESIGN_PACKAGE_LOOK } from '../../../../constants/design-package-look';
import type { TextureInfo } from '../../../../model/texture-info';
import type { TextureManager } from '../texture-manager.class';
import { BASE_EMISSIVE } from '../types';

export interface CreateMaterialParams {
	textureInfo: TextureInfo;
}

/**
 * Create a material from TextureInfo
 */
export const createMaterialCfn = (
	self: TextureManager,
	params: CreateMaterialParams
): MeshStandardMaterial | MeshPhysicalMaterial => {
	const { textureInfo: tx } = params;
	const forceEnvMapIntensity = DESIGN_PACKAGE_LOOK && DESIGN_PACKAGE_FORCE_ENVMAP_INTENSITY;
	const config = self.config;
	const textures = self.textures;
	const normalMaps = self.normalMaps;
	const alphaMaps = self.alphaMaps;
	const displacementMaps = self.displacementMaps;
	const environmentMap = self.environmentMap;

	const materialConfig: Record<string, unknown> = {
		normalScale: tx.normalScale,
		metalness: tx.metalness ?? 0.2,
		roughness: tx.roughness ?? 0.8,
		transparent: tx.transparent ?? false,
		envMapIntensity: forceEnvMapIntensity ? 1.0 : (tx.envMapIntensity ?? 0),
		side: FrontSide,
		wireframe: config.qualitySettings?.wireframe ?? false,
		polygonOffset: true,
		polygonOffsetFactor: 1,
		polygonOffsetUnits: 1
	};

	// Only set emissive properties if brightness is not null
	if (tx.brightness != null) {
		materialConfig.emissive = new Color(0xffffff);
		materialConfig.emissiveIntensity = tx.brightness - BASE_EMISSIVE;
	}

	// Determine if material should use metallic treatment
	const isMetallic = tx.metalness != null && tx.metalness > 0.5;
	let material: MeshStandardMaterial | MeshPhysicalMaterial;

	if (isMetallic) {
		// Use MeshPhysicalMaterial for metallic surfaces to enable clearcoat
		// Roughness 0.28-0.38 recommended for stainless steel to avoid banding
		// Clearcoat adds subtle surface reflection layer for realistic appearance
		// Reduced normalScale (0.3) prevents reflection ridges that cause banding under HDR lighting
		material = new MeshPhysicalMaterial({
			...materialConfig,
			normalScale: new Vector2(0.3, 0.3),
			roughness: tx.roughness ?? 0.32,
			envMapIntensity: forceEnvMapIntensity ? 1.0 : Math.min(tx.envMapIntensity ?? 1.0, 2.5),
			dithering: true,
			// Clearcoat prevents banding in metallic reflections
			clearcoat: tx.clearcoat ?? 0.15,
			clearcoatRoughness: tx.clearcoatRoughness ?? 0.25
		} as any);
	} else {
		material = new MeshStandardMaterial(materialConfig as any);
	}

	// Set environment map
	if (environmentMap) {
		const qualityAllowsEnvMap = config.qualitySettings?.enableEnvironmentMap ?? true;
		if (isMetallic || qualityAllowsEnvMap) {
			material.envMap = environmentMap;
		}
	}

	// Apply texture maps
	if (textures.has(tx.key)) {
		const texture = textures.get(tx.key)!;
		material.map = texture;
		material.emissiveMap = texture;
		// When a color tint is specified, apply it as a multiplicative tint over the texture.
		// Otherwise default to white (no tint).
		material.color = tx.color != null ? tx.color : new Color(0xffffff);
	} else if (tx.color != null) {
		material.color = tx.color;
	}

	if (normalMaps.has(tx.key)) {
		material.normalMap = normalMaps.get(tx.key)!;
	}

	if (alphaMaps.has(tx.key)) {
		material.alphaMap = alphaMaps.get(tx.key)!;
	}

	if (displacementMaps.has(tx.key)) {
		material.displacementMap = displacementMaps.get(tx.key)!;
		material.displacementScale = tx.displacementScale;
		material.displacementBias = tx.displacementBias;
	}

	if (tx.baseColor != null) {
		material.userData.baseColor = tx.baseColor;
	}

	// Name carries the texture key into glTF extras for downstream verifiability
	if (!material.name && tx.key) material.name = tx.key;

	material.needsUpdate = true;
	return material;
};
